import languages from '@/utils/language'
// 查找翻译key的函数
function findTranslationKey(text) {
  // 将文本中的具体变量值替换为 ${} 进行匹配
  const patternText = text.replace(/\${.*?}/g, '${}')

  // 遍历语言包查找匹配的key
  for (const [key, value] of Object.entries(languages)) {
    if (value && typeof value === 'object' && (value.zh === patternText || value.en === patternText)) {
      return key
    }
  }
  return null
}

// 添加变量替换函数
function replaceVariables(text, variables) {
  if (!variables) return text
  return text.replace(/\{(\w+)\}/g, (match, key) => {
    return variables[key] !== undefined ? variables[key] : match
  })
}

// 自动翻译函数
function autoTranslate(text, vm) {
  // 保存原始文本中的变量值
  const variables = []
  text.replace(/\${(.*?)}/g, (match, expr) => {
    // 将模板字符串中的表达式结果存入数组
    variables.push(expr)
    return match
  })

  // 将文本转换为模式进行匹配
  const patternText = text.replace(/\${.*?}/g, '${}')
  const key = findTranslationKey(patternText)

  if (key) {
    let translatedText = vm.$translate(key)
    // 将翻译文本中的 ${} 依次替换为实际的变量值
    let variableIndex = 0
    return translatedText.replace(/\${}/g, () => {
      const expr = variables[variableIndex++]
      // 执行变量表达式
      try {
        // 这里需要在实际环境中执行表达式，可能需要根据具体情况调整
        return eval(expr)
      } catch (e) {
        return expr
      }
    })
  }
  return text
}

// 翻译元素及其子元素
function translateElement(element, vm) {
  if (element.dataset.noTranslate) return
  
  // 翻译文本节点
  const translateTextNodes = (node) => {
    if (node.nodeType === 3) { // 文本节点
      translateTextNode(node, vm)
    } else if (node.nodeType === 1 && !node.dataset.noTranslate) { // 元素节点
      Array.from(node.childNodes).forEach(child => translateTextNodes(child))
    }
  }

  translateTextNodes(element)
}

// 翻译单个文本节点
function translateTextNode(textNode, vm) {
  const text = textNode.textContent.trim()
  if (text) {
    const key = findTranslationKey(text)
    if (key) {
      textNode.textContent = vm.$translate(key)
    }
  }
}

export default {
  install(Vue) {
    // 注册 v-no-translate 指令
    Vue.directive('no-translate', {
      bind(el) {
        el.dataset.noTranslate = 'true'
      }
    })

    Vue.prototype.$enableTemplateTranslate = false
    Vue.prototype.$enableMessageTranslate = false

    // 修改消息代理处理器
    function createMessageProxy(vm, original, type) {
      return new Proxy(original, {
        get(target, prop) {
          const originalMethod = target[prop]
          if (typeof originalMethod === 'function') {
            return (...args) => {
              if (!vm.getMessageTranslateState()) {
                return originalMethod.apply(target, args)
              }

              if (type === 'Notice') {
                const options = args[0]
                if (typeof options === 'string') {
                  return originalMethod.call(target, {
                    title: autoTranslate('提示', vm),
                    desc: autoTranslate(options, vm)
                  })
                }
                // 提取变量
                const variables = {}
                const descText = options.desc
                const matches = descText.match(/\{(\w+)\}/g)
                if (matches) {
                  matches.forEach(match => {
                    const key = match.slice(1, -1)
                    // 从原始文本中提取变量值
                    const value = descText.match(new RegExp(`${key}\\s*([^\\s}]+)`))
                    if (value) {
                      variables[key] = value[1]
                    }
                  })
                }
                return originalMethod.call(target, {
                  ...options,
                  title: autoTranslate(options.title || '提示', vm),
                  desc: autoTranslate(options.desc, vm, variables)
                })
              } else if (type === 'Message') {
                const text = args[0]
                const variables = {}
                const matches = text.match(/\{(\w+)\}/g)
                if (matches) {
                  matches.forEach(match => {
                    const key = match.slice(1, -1)
                    const value = text.match(new RegExp(`${key}\\s*([^\\s}]+)`))
                    if (value) {
                      variables[key] = value[1]
                    }
                  })
                }
                return originalMethod.call(target, autoTranslate(text, vm, variables))
              } else if (type === 'Modal') {
                const options = args[0]
                return originalMethod.call(target, {
                  ...options,
                  title: autoTranslate(options.title, vm),
                  content: autoTranslate(options.content, vm)
                })
              }
              return originalMethod.apply(target, args)
            }
          }
          return originalMethod
        }
      })
    }

    Vue.mixin({
      beforeCreate() {
        this._translatedNodes = new Set()
      },

      methods: {
        getTemplateTranslateState() {
          return this.enableTemplateTranslate !== undefined 
            ? this.enableTemplateTranslate 
            : this.$enableTemplateTranslate
        },

        getMessageTranslateState() {
          return this.enableMessageTranslate !== undefined 
            ? this.enableMessageTranslate 
            : this.$enableMessageTranslate
        },

        translateTextNode(textNode) {
          if (!this.getTemplateTranslateState()) return

          const text = textNode.textContent.trim()
          if (text) {
            const key = findTranslationKey(text)
            if (key) {
              textNode.originalText = text
              textNode.textContent = this.$translate(key)
              this._translatedNodes.add(textNode)
            }
          }
        },

        translateElement(element) {
          if (!this.getTemplateTranslateState() || element.dataset.noTranslate) return
          
          const walk = document.createTreeWalker(
            element,
            NodeFilter.SHOW_TEXT,
            null,
            false
          )

          let node
          while (node = walk.nextNode()) {
            this.translateTextNode(node)
          }
        }
      },

      mounted() {
        if (this.getTemplateTranslateState()) {
          this.translateElement(this.$el)
        }

        // 代理消息组件
        if (this.$Notice) {
          this.$Notice = createMessageProxy(this, this.$Notice, 'Notice')
        }
        if (this.$Message) {
          this.$Message = createMessageProxy(this, this.$Message, 'Message')
        }
        if (this.$Modal) {
          this.$Modal = createMessageProxy(this, this.$Modal, 'Modal')
        }
      },

      watch: {
        '$store.state.app.language'() {
          if (this.getTemplateTranslateState()) {
            this._translatedNodes.forEach(node => {
              if (node.parentNode) {
                const text = node.originalText || node.textContent.trim()
                const key = findTranslationKey(text)
                if (key) {
                  node.textContent = this.$translate(key)
                }
              }
            })
          }
        }
      }
    })
  }
}
